import { FormattedMessage, useIntl } from 'react-intl'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import ClearIcon from '@mui/icons-material/Clear'
import Flex from '../../../shared/components/layouts/Flex'
import { useNodes } from '../../../contexts/NodesContext'
import { useProjects } from '../../../contexts/ProjectsContext'
import { useServices } from '../../../contexts/ServicesContext'
import { get } from 'lodash'

const TASK_TYPES = [
  'TYPE::START_SERVICE',
  'TYPE::STOP_SERVICE',
  'TYPE::RESTART_SERVICE',
  'TYPE::DEPLOY_PROJECT',
]

const TASK_STATUSES = ['pending', 'running', 'completed', 'failed']

function TaskFilters({ filters, onChange, onClear }) {
  const { formatMessage } = useIntl()
  const { nodes } = useNodes()
  const { projects } = useProjects()
  const { services, servicesByProjectId } = useServices()

  const handleChange = (field) => (event) => {
    onChange({ ...filters, [field]: event.target.value })
  }

  const handleDateChange = (field) => (value) => {
    onChange({ ...filters, [field]: value })
  }

  const filteredServices = filters.projectId
    ? get(servicesByProjectId, filters.projectId, [])
    : services

  return (
    <Flex gap={2} alignItems="center" sx={{ flexWrap: 'wrap', mb: 2 }}>
      <TextField
        select
        size="small"
        label={<FormattedMessage id="tasks.filters.status" />}
        value={filters.status || ''}
        onChange={handleChange('status')}
        sx={{ minWidth: 140 }}
      >
        <MenuItem value="">
          <FormattedMessage id="tasks.filters.all" />
        </MenuItem>
        {TASK_STATUSES.map((status) => (
          <MenuItem key={status} value={status}>
            <FormattedMessage id={`tasks.status.${status}`} />
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label={<FormattedMessage id="tasks.filters.type" />}
        value={filters.taskType || ''}
        onChange={handleChange('taskType')}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="">
          <FormattedMessage id="tasks.filters.all" />
        </MenuItem>
        {TASK_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            <FormattedMessage id={`tasks.type.${type}`} />
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label={<FormattedMessage id="tasks.filters.node" />}
        value={filters.nodeId || ''}
        onChange={handleChange('nodeId')}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="">
          <FormattedMessage id="tasks.filters.all" />
        </MenuItem>
        {nodes.map((node) => (
          <MenuItem key={node.id} value={node.id}>
            {node.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label={<FormattedMessage id="tasks.filters.project" />}
        value={filters.projectId || ''}
        onChange={(e) => {
          onChange({
            ...filters,
            projectId: e.target.value,
            serviceId: '',
          })
        }}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="">
          <FormattedMessage id="tasks.filters.all" />
        </MenuItem>
        {projects.map((project) => (
          <MenuItem key={project.id} value={project.id}>
            {project.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label={<FormattedMessage id="tasks.filters.service" />}
        value={filters.serviceId || ''}
        onChange={handleChange('serviceId')}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="">
          <FormattedMessage id="tasks.filters.all" />
        </MenuItem>
        {filteredServices.map((service) => (
          <MenuItem key={service.id} value={service.id}>
            {service.name}
          </MenuItem>
        ))}
      </TextField>

      <DatePicker
        label={formatMessage({ id: 'tasks.filters.dateFrom' })}
        value={filters.dateFrom || null}
        onChange={handleDateChange('dateFrom')}
        slotProps={{
          textField: {
            size: 'small',
            sx: { minWidth: 160 },
          },
        }}
      />

      <DatePicker
        label={formatMessage({ id: 'tasks.filters.dateTo' })}
        value={filters.dateTo || null}
        onChange={handleDateChange('dateTo')}
        slotProps={{
          textField: {
            size: 'small',
            sx: { minWidth: 160 },
          },
        }}
      />

      <Button
        variant="outlined"
        size="small"
        startIcon={<ClearIcon />}
        onClick={onClear}
      >
        <FormattedMessage id="tasks.filters.clear" />
      </Button>
    </Flex>
  )
}

export default TaskFilters
